import { useMemo } from "react";

import { useTranslationContext } from "context/TranslationContext";
import cn from "lib/cn";

import { TTabItem } from "./types";

import styles from "./Tab.module.css";

export interface TabProps {
  id?: string;
  items: TTabItem;
  selected?: string;
  buttonClassName?: string;
  onChange?: (key: string) => void;
}

const Tab = ({ id, items, selected, buttonClassName, onChange }: TabProps) => {
  const { t } = useTranslationContext();

  const item = useMemo(() => {
    let tmpSelected = selected;
    let children = null;
    if (!selected) {
      const tabKeys = Object.keys(items);
      tmpSelected = tabKeys?.[0];
    }

    if (tmpSelected) {
      children = items[tmpSelected]?.children || null;
    }

    return {
      selected: tmpSelected,
      children: children,
    };
  }, [selected, items]);

  return (
    <div data-testid={id} id={id} className="w-full h-full">
      <div
        className={cn(
          "flex justify-left flex-nowrap gap-6 items-center h-full",
          styles.container
        )}
      >
        {Object.keys(items).map((key) => (
          <button
            data-testid={id ? `${id}-${key}` : undefined}
            key={key}
            className={cn(
              styles.label,
              "text-gray-900 h-full flex flex-col text-base",
              key === item.selected && "text-primary-500 font-semibold",
              buttonClassName
            )}
            onClick={
              onChange && key !== item.selected
                ? () => onChange(key)
                : undefined
            }
          >
            <div className="flex items-center h-full">
              <div
                className={cn(
                  "flex items-center justify-start gap-1",
                  key === item.selected ? "mt-1" : undefined
                )}
              >
                {items[key]?.icon || null}
                {t(items[key].label)}
              </div>
            </div>
            {key === item.selected && <div className={styles.activeTab} />}
          </button>
        ))}
      </div>

      <div data-testid={id ? `${id}-children` : undefined} className="w-full">
        {item.children}
      </div>
    </div>
  );
};

export default Tab;
